import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { useSession } from '../auth/session';
import { getSolConversationPointerStore } from '../../data/sol/sol-conversation-pointer';
import { reportError } from '../../lib/monitoring';
import type { SolRestoredMessage } from '../../data/queries/use-sol-conversations';
import type { SolExchange } from './sol-exchange';

/**
 * THE CURRENT SOL VISIT, HELD ABOVE THE SCREEN. M9 / S09-04.
 *
 * **THIS IS NAVIGATION CONTINUITY. IT IS NOT CONVERSATIONAL MEMORY**, and the
 * difference is the whole reason this file is written the way it is.
 *
 * ---------------------------------------------------------------------------
 * THE DEFECT IT EXISTS TO FIX, AS FOUND ON A REAL PHONE
 * ---------------------------------------------------------------------------
 *
 * Brandon asked Sol a question, got an answer citing two Captures, tapped the
 * first to check it, and pressed Back. **The answer was gone.** To look at the
 * second piece of evidence he had to retype the question and pay for a second
 * reasoning call — for evidence Crewlink had already found and already shown
 * him. Checking the sources is not a new task; **it is the same task**, and the
 * screen was throwing the work away in the middle of it.
 *
 * The cause was ordinary: the whole visit lived in `useState` inside the Sol
 * screen, and leaving the route unmounted it. So the visit moved up here, above
 * the routes, where navigating cannot destroy it.
 *
 * ---------------------------------------------------------------------------
 * WHAT MAKES THIS NOT A CHAT HISTORY
 * ---------------------------------------------------------------------------
 *
 * **THE VISIT ITSELF IS MEMORY AND NOTHING ELSE** — no database, no Supabase
 * storage, no filesystem, no `localStorage`, no cookie. A refresh, an app
 * restart and a sign-out all end it without any code having to remember to
 * clean up.
 *
 * **ONE THING IS PERSISTED, AND ONLY ONE (M11 / I-065): the id of the
 * conversation being continued.** Not the exchanges, not an answer, not a fact
 * — one server-issued identifier, so that reopening a killed app can ask the
 * server *"which conversation am I continuing?"* instead of silently starting
 * over. It is written through `sol-conversation-pointer.ts`, which explains at
 * length why an identifier is not authority and not memory. Everything else on
 * this page still dies with the process.
 *
 * **AND — THE PART THAT ACTUALLY MATTERS — NOTHING HERE IS EVER SENT TO SOL.**
 * Every request carries one question and the Project on screen. An earlier
 * exchange is something Brandon can look at; it is never something the model is
 * told. Preserving a visit changes what the SCREEN remembers, and changes
 * nothing about what SOL knows.
 *
 * ---------------------------------------------------------------------------
 * WHY IT IS KEYED BY PROJECT AND NOT BY ROUTE
 * ---------------------------------------------------------------------------
 *
 * The obvious implementation is to notice "he went to a Capture, so keep the
 * state" — matching the route he navigated to. **That is exactly the brittle
 * hack to avoid**: it breaks the first time a source card points at a Reminder,
 * a Report or a future Sol domain, and it breaks silently, by throwing away work
 * again.
 *
 * So nothing here knows about routes. The visit simply survives navigation, and
 * its lifetime is bounded by three things a person can reason about: **the tab,
 * the Project, and an explicit "Start over".** Asking under a different Project
 * replaces the visit, because it is a different visit.
 */

/** Generated audio, held for the current visit only. Bytes, never a URL. */
export interface SolSessionAudio {
  readonly bytes: ArrayBuffer;
  readonly mimeType: string;
}

interface StoredVisit {
  /** The Project this visit belongs to. Null on a future global Sol. */
  readonly projectId: string | null;
  readonly exchanges: readonly SolExchange[];
  readonly draft: string;
  /**
   * The durable conversation these exchanges belong to (M11), or null before
   * the first answer.
   *
   * **AN IDENTIFIER, AND ONLY AN IDENTIFIER.** What the conversation contains
   * lives on the server; this is the token that lets the next question continue
   * it, and it is what **Start over** drops — which is the whole of what Start
   * over means. The visit is still not conversational memory: the browser still
   * sends no earlier question, answer, cursor or filter with anything.
   */
  readonly conversationId: string | null;
  /**
   * The next exchange id. **Kept in the visit, not in the screen**, so a
   * restored visit cannot mint an id that collides with one already in it.
   */
  readonly nextId: number;
  /**
   * The STORED messages of `conversationId`, drawn back from the server (M11 /
   * D-041) — what puts the visible bubbles back after an app restart or when
   * an earlier conversation is reopened. Null until loaded. **Held in the
   * visit so navigating away and back does not re-read them**, and so they are
   * never loaded twice into the same screen.
   *
   * **WHAT WAS SAID, FOR A PERSON TO SEE. NOT MEMORY FOR SOL**: nothing here
   * is sent with a question; the server decides, bounded, what Sol reads.
   */
  readonly restored: readonly SolRestoredMessage[] | null;
  /** The conversation `restored` belongs to, so a stale load is never adopted. */
  readonly restoredFor: string | null;
}

const EMPTY: StoredVisit = {
  projectId: null,
  exchanges: [],
  draft: '',
  nextId: 1,
  conversationId: null,
  restored: null,
  restoredFor: null,
};

export interface SolSessionValue {
  /** The visit for this Project, or an empty one. **A pure read.** */
  visit(projectId: string | null): {
    readonly exchanges: readonly SolExchange[];
    readonly draft: string;
    /** The durable conversation to continue, or null to begin one. */
    readonly conversationId: string | null;
    /** Stored messages of the conversation, once loaded; null before. */
    readonly restored: readonly SolRestoredMessage[] | null;
    readonly restoredFor: string | null;
  };
  /**
   * **REOPEN AN EARLIER CONVERSATION** (M11 / D-041). Replaces this Project's
   * visit with an empty one pointed at `conversationId`, and records the
   * pointer so a relaunch continues it. The stored messages are then loaded
   * and shown by the screen; a new question continues that conversation.
   * Nothing is deleted and nothing is sent.
   */
  openConversation(projectId: string | null, conversationId: string): void;
  /** File the stored messages loaded for `conversationId`. Ignored if the visit moved on. */
  setRestored(
    projectId: string | null,
    conversationId: string,
    messages: readonly SolRestoredMessage[],
  ): void;
  /**
   * Remember which conversation the server put this turn in.
   *
   * **THE SERVER DECIDES THE ID; THE VISIT ONLY CARRIES IT.** The browser never
   * mints one, so a client that guessed an id would be guessing at a row RLS
   * will refuse it anyway.
   */
  setConversationId(projectId: string | null, conversationId: string): void;
  /**
   * **THE RELAUNCH PATH (I-065).** Look on the device for the conversation this
   * owner was continuing under this Project, adopt it into the visit, and
   * return it.
   *
   * Null means "begin a new conversation" and is always safe: a signed-out app,
   * another person's record, another Project's record, an unreadable store and
   * a device that never had one all answer null, and the caller behaves exactly
   * as it did before recovery existed. **It returns the id rather than only
   * storing it** because the question in flight needs it in the same tick — a
   * value that arrives on the next render is a question already sent without
   * it.
   */
  recoverConversationId(projectId: string | null): Promise<string | null>;
  setDraft(projectId: string | null, draft: string): void;
  /**
   * Put words back in the composer, but only if it is still empty.
   *
   * **IT MUST BE DECIDED AGAINST THE LATEST DRAFT, NOT A CAPTURED ONE.** This
   * runs when a request has already failed, which is long after the caller's
   * copy of the visit was read — so the emptiness check happens inside the
   * update, where `previous` is current. Reading it outside restored his words
   * over words he had since typed, or not at all.
   */
  restoreDraftIfEmpty(projectId: string | null, draft: string): void;
  /** Append an answered exchange. Ids come from `takeId`. */
  addExchange(projectId: string | null, exchange: SolExchange): void;
  /** Drop the newest exchange — the retry path replacing a failed answer. */
  dropNewest(projectId: string | null): void;
  /** Reserve the next monotonic exchange id for this Project's visit. */
  takeId(projectId: string | null): number;
  /** End the visit deliberately. Also destroys any generated audio. */
  clear(projectId: string | null): void;
  /**
   * Generated Read Aloud audio for the current visit.
   *
   * **A STABLE `Map`, DELIBERATELY OUTSIDE REACT STATE.** Audio arriving must
   * not re-render the screen, and an `ArrayBuffer` is not something to put in a
   * state tree. It is emptied by `clear`, by a Project change and by sign-out —
   * the same three boundaries that end the visit.
   */
  readonly audio: Map<string, SolSessionAudio>;
  /**
   * **PROPOSALS THAT HAVE ALREADY BEEN ANSWERED, BY TICKET.**
   *
   * The terminal outcome of a confirmation card used to live in the card's own
   * `useState`. Navigating away unmounted it, and coming back re-mounted a
   * fresh card at "offered" — so **a change Brandon had already confirmed
   * appeared to be waiting for him again**, with live Confirm and Cancel
   * buttons, while the Capture was long since REPAIRED. A card that re-offers a
   * completed action teaches him to tap buttons that mean nothing.
   *
   * So the fact lives here instead, with the exchanges it belongs to, and the
   * card reads it on mount. **The stored value is the sentence already shown** —
   * no new wording, no status recomputed on the client, nothing that could
   * contradict the server. Same lifetime as `audio`: the tab, the Project and
   * Start over.
   *
   * **THIS IS DISPLAY MEMORY, NOT AUTHORITY.** It cannot cause a write, cannot
   * prevent one, and is never sent anywhere. The server is what refuses a
   * second write, and it still does — an expired or replayed ticket is refused
   * on its own merits whatever this map says.
   */
  readonly settledActions: Map<string, SolSettledAction>;
}

/** What a confirmation card showed once it was finished with. */
export interface SolSettledAction {
  /** `done` — the write happened. `stopped` — cancelled, expired or refused. */
  readonly kind: 'done' | 'stopped';
  readonly text: string;
}

const SolSessionContext = createContext<SolSessionValue | null>(null);

/**
 * Mounted ABOVE the routes, inside the auth session.
 *
 * Inside, because signing out must end the visit — Brandon's question and
 * Crewlink's answer are his, and they do not outlive his session. Above the
 * routes, because that is the entire point.
 *
 * **SIGN-OUT AND USER-SWITCH ARE HANDLED BY `key`, NOT BY BOOKKEEPING.**
 * Remounting on a changed user id discards the visit and the audio together, in
 * one step React performs itself. The alternative — comparing the previous user
 * during render, or clearing inside an effect — either reaches for a ref where a
 * ref does not belong, or renders the previous user's question once before
 * clearing it. That one frame is the one that must not exist.
 */
export function SolSessionProvider({ children }: { readonly children: ReactNode }) {
  const { session } = useSession();
  const ownerUserId = session?.user.id ?? null;
  return (
    <SolVisitState key={ownerUserId ?? 'signed-out'} ownerUserId={ownerUserId}>
      {children}
    </SolVisitState>
  );
}

function SolVisitState({
  children,
  ownerUserId,
}: {
  readonly children: ReactNode;
  readonly ownerUserId: string | null;
}) {
  const value = useSolVisitStore(ownerUserId);
  return <SolSessionContext.Provider value={value}>{children}</SolSessionContext.Provider>;
}

/**
 * The visit, or a **screen-local fallback** when no provider is mounted.
 *
 * The fallback is not a convenience for tests — it is what keeps the Sol screen
 * mountable on its own, and it fails in the safe direction: without a provider
 * the visit simply does not survive navigation, exactly as it behaved before.
 */
export function useSolSession(): SolSessionValue {
  const provided = useContext(SolSessionContext);
  // Built unconditionally — hooks may not be called conditionally — and simply
  // ignored whenever a provider is present. **It is given no owner**, so a
  // screen mounted without a provider persists nothing and recovers nothing —
  // the same safe direction the fallback has always failed in.
  const fallback = useSolVisitStore(null);
  return provided ?? fallback;
}

/** One implementation, used by the provider and by the fallback alike. */
function useSolVisitStore(ownerUserId: string | null): SolSessionValue {
  const [visit, setVisit] = useState<StoredVisit>(EMPTY);
  // `useState` rather than `useRef`: the identity must be stable for the life of
  // the store, and it is read during render to build the value below.
  const [audio] = useState(() => new Map<string, SolSessionAudio>());
  // Same shape and the same three boundaries as `audio`, for the same reason:
  // a settled card must not re-render the screen, and it must die with the
  // visit it belongs to.
  const [settledActions] = useState(() => new Map<string, SolSettledAction>());

  const write = useCallback(
    (projectId: string | null, change: (previous: StoredVisit) => StoredVisit) => {
      setVisit((previous) => {
        // **A DIFFERENT PROJECT IS A DIFFERENT VISIT.** Replacing rather than
        // merging is what stops one building's answers appearing under another.
        if (previous.projectId !== projectId) {
          audio.clear();
          settledActions.clear();
          return change({ ...EMPTY, projectId });
        }
        return change(previous);
      });
    },
    [audio, settledActions],
  );

  return useMemo<SolSessionValue>(
    () => ({
      audio,
      settledActions,

      visit: (projectId) =>
        visit.projectId === projectId
          ? {
              exchanges: visit.exchanges,
              draft: visit.draft,
              conversationId: visit.conversationId,
              restored: visit.restored,
              restoredFor: visit.restoredFor,
            }
          : {
              exchanges: EMPTY.exchanges,
              draft: EMPTY.draft,
              conversationId: EMPTY.conversationId,
              restored: EMPTY.restored,
              restoredFor: EMPTY.restoredFor,
            },

      openConversation: (projectId, conversationId) => {
        audio.clear();
        settledActions.clear();
        // **A FRESH VISIT, POINTED AT THE OLD CONVERSATION.** Whatever was on
        // screen for this Project is replaced — it belongs to a different
        // conversation — and the stored messages of this one load next.
        setVisit({ ...EMPTY, projectId, conversationId });
        if (ownerUserId !== null) {
          void pointerStore()
            .remember({ ownerUserId, projectId, conversationId })
            .catch((error: unknown) => reportError(error, 'sol.rememberConversation'));
        }
      },

      setRestored: (projectId, conversationId, messages) => {
        write(projectId, (previous) =>
          // Only for the conversation the visit is actually on: a load that
          // resolves after Start Over or after another conversation was
          // opened is simply dropped.
          previous.conversationId === conversationId
            ? { ...previous, restored: messages, restoredFor: conversationId }
            : previous,
        );
      },

      setConversationId: (projectId, conversationId) => {
        write(projectId, (previous) => ({ ...previous, conversationId }));
        // **REMEMBERED FOR THE NEXT PROCESS, NOT FOR THIS ONE.** The visit
        // above already has it; this is what survives the app being killed.
        // Deliberately not awaited: the answer is already on the screen, and a
        // slow disk must not hold it there. A failure loses recovery only.
        if (ownerUserId !== null) {
          void pointerStore()
            .remember({ ownerUserId, projectId, conversationId })
            .catch((error: unknown) => reportError(error, 'sol.rememberConversation'));
        }
      },

      recoverConversationId: async (projectId) => {
        if (ownerUserId === null) return null;
        let recovered: string | null = null;
        try {
          recovered = await pointerStore().read(ownerUserId, projectId);
        } catch (error) {
          // The port already swallows its own failures; this is the belt to its
          // braces. **Recovery never fails a question** — it declines.
          reportError(error, 'sol.recoverConversation');
          return null;
        }
        if (recovered === null) return null;
        // Adopted into the visit so later questions in this process do not read
        // the device again, and so `visit()` and the request agree.
        write(projectId, (previous) =>
          previous.conversationId === null ? { ...previous, conversationId: recovered } : previous,
        );
        return recovered;
      },

      setDraft: (projectId, draft) => {
        write(projectId, (previous) => ({ ...previous, draft }));
      },

      restoreDraftIfEmpty: (projectId, draft) => {
        write(projectId, (previous) =>
          previous.draft.length === 0 ? { ...previous, draft } : previous,
        );
      },

      addExchange: (projectId, exchange) => {
        write(projectId, (previous) => ({
          ...previous,
          exchanges: [...previous.exchanges, exchange],
        }));
      },

      dropNewest: (projectId) => {
        write(projectId, (previous) => ({
          ...previous,
          exchanges: previous.exchanges.slice(0, -1),
        }));
      },

      takeId: (projectId) => {
        // Read before the write, so the caller gets the id it just reserved.
        const id = visit.projectId === projectId ? visit.nextId : EMPTY.nextId;
        write(projectId, (previous) => ({ ...previous, nextId: previous.nextId + 1 }));
        return id;
      },

      clear: (projectId) => {
        audio.clear();
        settledActions.clear();
        setVisit((previous) => (previous.projectId === projectId ? EMPTY : previous));
        // **START OVER MEANS "CONTINUE NO LONGER", NOT "DELETE HISTORY".** The
        // pointer goes, so a relaunch cannot walk back into the conversation he
        // deliberately ended. The stored conversation itself is untouched —
        // nothing here can delete one, and nothing asks the server to.
        if (ownerUserId !== null) {
          void pointerStore()
            .forget(ownerUserId)
            .catch((error: unknown) => reportError(error, 'sol.forgetConversation'));
        }
      },
    }),
    [audio, ownerUserId, settledActions, visit, write],
  );
}

/**
 * Resolved at call time, never at import time.
 *
 * The store opens a database on first use, and a module-level call would open
 * one merely because this file was imported — including inside a test that
 * never touches Sol.
 */
function pointerStore() {
  return getSolConversationPointerStore();
}
